import { type Transition } from "motion/react";

/**
 * Shared motion language for content appearing in the conversation.
 *
 * Everything animates on `opacity` and `transform` only: `components/messages` pins a
 * message to the top of the viewport from its measured position, so an animation touching
 * layout would fight the scrolling. Transforms are fine: the pin measures a message without
 * its own transform (`getLayoutTop`), so the same entrance plays in every scroll mode.
 * The one exception is inline buttons closing the room they leave behind (see
 * `INLINE_BUTTONS_COLLAPSE_TRANSITION`): that is the layout change itself, which the pins
 * re-measure as it goes.
 */

/**
 * The pace of one kind of element. Every knob worth turning lives in one of the two
 * profiles below — change a number there, not in the components.
 *
 * - `fadeDurationS` lands before the glide does, so content is readable while it settles.
 * - `spring` carries the travel. Lower `stiffness` = slower; lower `damping` = more
 *   bounce. Keep `damping` near 0.8 × 2√(stiffness × mass) to preserve the current feel:
 *   that ratio is what makes it settle with a hint of overshoot instead of wobbling.
 *   An ease-out tween front-loads the distance so hard that an entrance reads as a
 *   flicker rather than a move, which is why this is a spring at all.
 * - `exitDurationS` is a plain ease-in, always quicker than the entrance: a spring on the
 *   way out overshoots past the element's resting place and reads as a glitch. Anything
 *   waiting on an exit also starts that much sooner.
 */
type RevealSpring = {
  type: "spring";
  stiffness: number;
  damping: number;
  mass: number;
};

type RevealTiming = {
  fadeDurationS: number;
  spring: RevealSpring;
  exitDurationS: number;
};

/** Settles in ~420ms. */
export const MESSAGE_REVEAL_TIMING: RevealTiming = {
  fadeDurationS: 0.3,
  spring: { type: "spring", stiffness: 180, damping: 22, mass: 1 },
  exitDurationS: 0.18,
};

/**
 * Settles in ~350ms — a touch brisker than a message. A button group is read as one
 * gesture and is waited on before the next tap, so it earns the quicker pace; a message
 * is content, and hurrying it in makes it feel flicked rather than delivered.
 */
export const BUTTON_REVEAL_TIMING: RevealTiming = {
  fadeDurationS: 0.25,
  spring: { type: "spring", stiffness: 260, damping: 26, mass: 1 },
  exitDurationS: 0.15,
};

/**
 * The footer slot plays its two halves in sequence — the outgoing one leaves before the
 * incoming one arrives — so its perceived duration is exit + entrance. It is paced
 * briskest of the three to keep that chain under half a second.
 * Settles in ~320ms, for ~460ms end to end.
 */
export const FOOTER_SLOT_TIMING: RevealTiming = {
  fadeDurationS: 0.2,
  // Damped at ~0.55, looser than the other two. The slot travels as one wide bar with no
  // scale and no cascade to give it life, so its overshoot is the only thing that reads as
  // bounce, and overshoot falls away steeply as damping rises (~13% of the travel here,
  // ~5% at 24, ~1% at 28).
  spring: { type: "spring", stiffness: 300, damping: 19, mass: 1 },
  exitDurationS: 0.12,
};

/**
 * Inline buttons leaving from under an answer (`showOnlyLast`) give back the room they held
 * once they have faded out: dropped in one go, it would jump everything below them up the
 * moment their node goes, and closing it during the fade slides the conversation up under
 * buttons that are still showing.
 */
const INLINE_BUTTONS_COLLAPSE_DURATION_S = 0.25;
export const INLINE_BUTTONS_COLLAPSE_TRANSITION: Transition = {
  duration: INLINE_BUTTONS_COLLAPSE_DURATION_S,
  ease: "easeInOut",
  delay: BUTTON_REVEAL_TIMING.exitDurationS,
};
/** Until inline buttons are fully gone, fade then collapse: what arrives after them waits this. */
export const INLINE_BUTTONS_EXIT_DURATION_S =
  BUTTON_REVEAL_TIMING.exitDurationS + INLINE_BUTTONS_COLLAPSE_DURATION_S;

/** How far each kind of element travels before landing, in px. */
export const MESSAGE_REVEAL_OFFSET_Y = 20;
export const BUTTON_REVEAL_OFFSET_Y = 16;
/**
 * The slot spans the footer, so it slides plainly and never scales: growing a full-width
 * bar into place reads as a zoom, and scaling a text input blurs its content mid-flight.
 */
export const FOOTER_SLOT_OFFSET_Y = 32;

/**
 * The scroll-to-bottom button is a small round affordance that comes and goes often, so it
 * pops rather than travels: barely any distance, and a deeper scale than a message button
 * because at that size the growth is what the eye catches.
 */
export const SCROLL_TO_BOTTOM_OFFSET_Y = 8;
export const SCROLL_TO_BOTTOM_SCALE_FROM = 0.8;

/**
 * Message actions are chrome rather than content, so they settle in just after the message
 * they belong to instead of arriving with it. Long enough to read as a second beat, short
 * enough not to feel late when the message it follows did not animate at all — which is the
 * case for a streamed answer, whose final message replaces its placeholder in silence.
 */
export const MESSAGE_ACTIONS_DELAY_S = 0.2;
/** Buttons are small enough that growing into place reads well; messages would look cheap. */
export const BUTTON_REVEAL_SCALE_FROM = 0.92;

/**
 * Buttons cascade instead of all landing at once. This compounds with the spring: the
 * last button of a group waits its stagger AND then travels, so it is the sharpest knob
 * on how long a whole group takes to arrive.
 */
const BUTTON_REVEAL_STAGGER_S = 0.06;
/** …but a long list must not keep its last button waiting. */
const MAX_BUTTON_REVEAL_DELAY_S = 0.36;

/**
 * Delay before a button joins the cascade.
 *
 * Only the batch appearing right now cascades: buttons already on screen are
 * excluded so a block using per-button delays doesn't stack a stagger on top of
 * the wait the bot already asked for.
 */
export const getButtonRevealDelayS = (position: number, alreadyRevealedCount: number): number =>
  Math.min(
    Math.max(0, position - alreadyRevealedCount) * BUTTON_REVEAL_STAGGER_S,
    MAX_BUTTON_REVEAL_DELAY_S
  );

type RevealTarget = { opacity: number; y: number; scale: number };

/** For animations that run outside `getRevealProps`, when `motionEnabled` is off. */
export const INSTANT_TRANSITION: Transition = { duration: 0 };

type RevealProps = {
  initial?: RevealTarget | false;
  animate?: RevealTarget;
  exit?: RevealTarget & { transition: Transition };
  transition?: Transition;
};

/**
 * Entrance of an element joining the conversation. Returns nothing when motion is
 * off, which leaves the `motion` component behaving like its plain HTML tag.
 */
export const getRevealProps = ({
  motionEnabled,
  timing,
  offsetY = 0,
  scaleFrom = 1,
  delayS = 0,
  withExit = false,
  skipEntrance = false,
}: {
  motionEnabled: boolean;
  /** The pace to move at. Passed explicitly so no caller silently inherits another kind's. */
  timing: RevealTiming;
  /**
   * Distance the element rises from. Anything measuring an animated element must leave its
   * transform out (see `getLayoutTop`) rather than have the entrance toned down for it.
   */
  offsetY?: number;
  /** Scale the element grows from. A scale moves the measured box too: same rule as above. */
  scaleFrom?: number;
  delayS?: number;
  /**
   * Also retreat on the way out. Only for elements sitting under an `AnimatePresence`
   * that outlives them — an exit holds the node mounted until it finishes, so a message
   * would linger on top of the one replacing it.
   */
  withExit?: boolean;
  /**
   * Mount at rest, e.g. a node whose content is already on screen. Only the entrance goes:
   * the element keeps its exit, and leaves the way every other one does.
   */
  skipEntrance?: boolean;
}): RevealProps => {
  if (!motionEnabled) {
    return {};
  }

  return {
    initial: skipEntrance ? false : { opacity: 0, y: offsetY, scale: scaleFrom },
    animate: { opacity: 1, y: 0, scale: 1 },
    ...(withExit
      ? {
          exit: {
            opacity: 0,
            y: offsetY,
            scale: scaleFrom,
            transition: { duration: timing.exitDurationS, ease: "easeIn" },
          },
        }
      : {}),
    transition: {
      opacity: { duration: timing.fadeDurationS, ease: "easeOut", delay: delayS },
      y: { ...timing.spring, delay: delayS },
      scale: { ...timing.spring, delay: delayS },
    },
  };
};
